/**
 * Google Tag Manager (GTM) Helper Functions
 * Provides type-safe dataLayer event tracking
 */

import { emitClientEvent } from '@/lib/analytics/emit-client-event';
import { getGaSessionId } from '@engine/attribution';

declare global {
  interface Window {
    dataLayer?: Array<Record<string, unknown>>;
  }
}

/**
 * Push event to GTM dataLayer
 */
export function pushToDataLayer(event: Record<string, unknown>): void {
  if (typeof window !== 'undefined' && window.dataLayer) {
    window.dataLayer.push(event);
  } else {
    console.warn('GTM dataLayer not available', event);
  }
}

/**
 * Also send the event server-side so it reaches GA4 (Measurement Protocol) even
 * when gtag/GTM is blocked client-side. Fire-and-forget; never throws.
 *
 * Routed through `emitClientEvent` rather than a hand-rolled fetch. This module
 * was the last client path bypassing the shared transport, which meant its two
 * events silently missed everything the transport adds: sendBeacon survival on
 * unload, the `wisprs_sid` session id, and the per-emit `eventId` that makes a
 * retry idempotent. `gaSessionId` still rides along explicitly because the GA4
 * provider needs gtag's numeric session, not our first-party one.
 */
function postServerEvent(name: string, props: Record<string, unknown>): void {
  emitClientEvent('/api/events/gtm', {
    name,
    gaSessionId: getGaSessionId(process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID),
    ...props,
  });
}

/**
 * Fire purchase_confirmed — a success-PAGE-VIEW diagnostic, not a payment.
 *
 * Deliberately carries NO monetary value. It used to hardcode `value: 25` for
 * every purchase regardless of plan or billing interval, so every Studio,
 * Agency and annual sale was reported to GA4 and Mixpanel at the Pro monthly
 * price. A wrong number is worse than none: it looks authoritative and silently
 * skews revenue reporting.
 *
 * Real revenue comes from the Polar webhook (lib/billing/order-paid-event.ts),
 * which has the actual net amount and currency. This event exists only to show
 * that a browser reached the success page, which is not proof of payment.
 */
export function trackPurchaseConfirmed(params: { checkoutId: string; plan: string }): void {
  pushToDataLayer({
    event: 'purchase_confirmed',
    app: 'wisprs',
    plan: params.plan,
    checkout_id: params.checkoutId,
  });
  postServerEvent('purchase_confirmed', {
    plan: params.plan,
    checkout_id: params.checkoutId,
  });
}

/**
 * Fire activation_confirmed event
 * Fired when user copies follow-up email (Sales Call Kit activation)
 */
export function trackActivationConfirmed(params: {
  activationType: 'followup_copied' | 'crm_note_copied' | 'next_steps_copied';
}): void {
  pushToDataLayer({
    event: 'activation_confirmed',
    app: 'wisprs',
    activation_type: params.activationType,
  });
  postServerEvent('activation_confirmed', { activation_type: params.activationType });
}
